import React from 'react';
import {useTranslation} from "react-i18next";
import {useTimeLimits as useItems} from "../../../contexts/TimeLimitContext";
import TimeLimit from "@customTypes/TimeLimit";
import {
    IonAccordion, IonButton,
    IonCheckbox,
    IonDatetime,
    IonDatetimeButton, IonIcon,
    IonItem,
    IonLabel,
    IonModal
} from "@ionic/react";
import TimeLimitData from "@customTypes/TimeLimitData";
import {addOutline, addSharp, closeCircle} from "ionicons/icons";

interface componentProps {
    formData:          TimeLimit
    setFormData:       React.Dispatch<React.SetStateAction<TimeLimit>>
    updateFormHandler: any
}

export const AccordionTimeLimitData: React.FC<componentProps> = ({
    formData, setFormData, updateFormHandler
}) => {

    const {t} = useTranslation()
    const {inProgress} = useItems()

    const weekDays: string[] = [
        'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
    ];

    const addTimeLimitData = (ev: any) => {
        let timeLimitData: TimeLimitData[] = formData.timeLimitData || []

        if (timeLimitData.length === 0) {
            timeLimitData.push({
                id: 0,
                externalId: null,
                timeLimitId: formData.id,
                lockingSystemId: formData.lockingSystemId,
                weekDayMonday: true,
                weekDayTuesday: true,
                weekDayWednesday: true,
                weekDayThursday: true,
                weekDayFriday: true,
                weekDaySaturday: false,
                weekDaySunday: false,
                startTime: '08:00',
                endTime: '18:00',
            })
        } else if (timeLimitData.length === 1) {
            timeLimitData.push({
                id: 0,
                externalId: null,
                timeLimitId: formData.id,
                lockingSystemId: formData.lockingSystemId,
                weekDayMonday: false,
                weekDayTuesday: false,
                weekDayWednesday: false,
                weekDayThursday: false,
                weekDayFriday: false,
                weekDaySaturday: true,
                weekDaySunday: true,
                startTime: '08:00',
                endTime: '18:00',
            })
        } else {
            timeLimitData.push({
                id: 0,
                externalId: null,
                timeLimitId: formData.id,
                lockingSystemId: formData.lockingSystemId,
                weekDayMonday: false,
                weekDayTuesday: false,
                weekDayWednesday: false,
                weekDayThursday: false,
                weekDayFriday: false,
                weekDaySaturday: false,
                weekDaySunday: false,
                startTime: '08:00',
                endTime: '18:00',
            })
        }

        setFormData({ ...formData, ['timeLimitData']: timeLimitData })
    }

    const removeTimeLimitData = (ev: any, index: number) => {
        ev.preventDefault()
        ev.stopPropagation()

        if (formData.timeLimitData && formData.timeLimitData.length > 0) {
            const newItems = [...formData.timeLimitData]
            newItems.splice(newItems.findIndex((oldItem, oldItemIndex) => oldItemIndex === index), 1)

            setFormData({ ...formData, ['timeLimitData']: newItems })
        }
    }

    const updateTimeLimitDataItemAttribute = (
        ev: any, timeLimitDataItemIndex: number, attribute: string, value: any
    ) => {
        ev.preventDefault()
        ev.stopPropagation()

        if (formData.timeLimitData && formData.timeLimitData.length > 0) {
            const newItems: TimeLimitData[] = formData.timeLimitData.map((oldItem: TimeLimitData, oldItemIndex: number) => {
                return (oldItemIndex === timeLimitDataItemIndex) ? {...oldItem, [attribute]: value} : oldItem
            })

            setFormData({ ...formData, ['timeLimitData']: newItems })
        }
    }

    const canAddTimeLimitData = (): boolean => {
        if (! formData.timeLimitData || formData.timeLimitData?.length === 0) {
            return true;
        }

        if (formData.timeLimitData?.length >= 10) {
            return false;
        }

        const lastItem: TimeLimitData = formData.timeLimitData[formData.timeLimitData.length - 1]

        const hasSelectedWeekDay = weekDays.find((weekDay: string, weekDayIndex: number) => {
            return lastItem['weekDay' + weekDay as keyof TimeLimitData]
        })

        return hasSelectedWeekDay !== undefined
    }

    return (
        <IonAccordion className="ion-padding-bottom" toggleIconSlot="start" value="timeLimitData">
            <IonItem className="bc-accordion" slot="header">
                <IonLabel>{t('time-limit-data.title_item')} (max. 10)</IonLabel>
            </IonItem>
            {formData.timeLimitData?.map(((timeLimitDataItem: TimeLimitData, timeLimitDataItemIndex: number) => {
                return (
                    <IonItem key={timeLimitDataItemIndex} slot="content">
                        {weekDays.map((weekDay: string, weekDayIndex: number) => {

                            let weekDayActive: boolean = timeLimitDataItem['weekDay' + weekDay as keyof TimeLimitData] as boolean

                            return (
                                <IonCheckbox alignment="center" checked={weekDayActive} value="1"
                                             key={weekDayIndex} labelPlacement="stacked"
                                             onClick={(ev: any) => {
                                                 updateTimeLimitDataItemAttribute(ev, timeLimitDataItemIndex, 'weekDay' + weekDay, ev.target.checked)
                                             }}>
                                    {t('common.weekDays.' + weekDay).substring(0, 2)}
                                </IonCheckbox>
                            )
                        })}
                        <span style={{ 'flex': '1 1 0', 'textAlign': 'center' }}>
                            <span>{t('common.from')}:</span>
                            <IonDatetimeButton datetime={"startTime_" + timeLimitDataItemIndex} />
                            <IonModal keepContentsMounted={true}>
                                <IonDatetime id={"startTime_" + timeLimitDataItemIndex}
                                             minuteValues="0,15,30,45"
                                             presentation="time"
                                             onIonChange={(ev) => {
                                                 updateTimeLimitDataItemAttribute(
                                                     ev, timeLimitDataItemIndex, 'startTime', ev.detail.value
                                                 )
                                             }}
                                             value={timeLimitDataItem.startTime} />
                            </IonModal>
                        </span>
                        <span style={{ 'flex': '1 1 0', 'textAlign': 'center' }}>
                            <span>{t('common.until')}:</span>
                            <IonDatetimeButton datetime={"endTime_" + timeLimitDataItemIndex} />
                            <IonModal keepContentsMounted={true}>
                                <IonDatetime id={"endTime_" + timeLimitDataItemIndex}
                                             minuteValues="0,15,30,45"
                                             presentation="time"
                                             onIonChange={(ev) => {
                                                 updateTimeLimitDataItemAttribute(
                                                     ev, timeLimitDataItemIndex, 'endTime', ev.detail.value
                                                 )
                                             }}
                                             value={timeLimitDataItem.endTime} />
                            </IonModal>
                        </span>
                        <span style={{ 'minWidth': '35px', 'flex': '1 1 0', 'textAlign': 'center' }}>
                            <IonButton color="medium" fill="clear" size="large"
                                       onClick={(ev) => removeTimeLimitData(ev, timeLimitDataItemIndex)}>
                                <IonIcon icon={closeCircle} slot="icon-only" />
                            </IonButton>
                        </span>
                    </IonItem>
                )
            }))}
            <IonItem className="ion-padding-horizontal ion-text-center" key="time-limit-add" slot="content">
                {canAddTimeLimitData() &&
                <IonButton color="primary" disabled={inProgress} expand="full" style={{ 'width': '100%' }}
                           fill="clear"
                           onClick={(ev) => addTimeLimitData(ev)}
                           type="button">
                    <IonIcon ios={addOutline} md={addSharp} slot="start" />
                    {t('common.add')}
                </IonButton>
                }
                {formData.timeLimitData && formData.timeLimitData?.length >= 10 &&
                    <span style={{ 'color': '#777', 'width': '100%' }}>Max. of 10</span>
                }
            </IonItem>
        </IonAccordion>
    )

}

export default AccordionTimeLimitData